import React, { Suspense, useRef, useState, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { useLoader } from '@react-three/fiber'
import {  useThree, useFrame } from '@react-three/fiber'
import { InterleavedBuffer, MeshBasicMaterial, XRHitTestTrackableType } from 'three';


function Particles({ count }) {
    const mesh = useRef()
    const gltf = useLoader(GLTFLoader, './hole.glb');
    const { mouse } = useThree();
    let lastTime = 0;
  
    const dummy = useMemo(() => new THREE.Object3D(), [])
    
    const particles = useMemo(() => {
      const temp = []
      for (let i = 0; i < count; i++) {
        const startLife = Math.random() * 3+0.3;
        const y = i%20;
        const randCircle = Math.round(Math.random()*20) * Math.PI * 2 / 20;
        const start = new THREE.Vector3(Math.sin(randCircle)* 3 + Math.random()/10,
                                        y,
                                        Math.cos(randCircle)* 3 + Math.random()/10);

        const randCircle2 = Math.round(Math.random()*20) * Math.PI * 2 / 20;
        const next = new THREE.Vector3(Math.sin(randCircle2) * 3 + Math.random()/10,
                                        y,
                                        Math.cos(randCircle2)* 3 + Math.random()/10);
        temp.push({ start, next, startLife, life: startLife });
      }
      return temp
    }, [count])
    
    useFrame(({clock}) => {
      if(!mesh.current) return;
      
      // Go through each particle
      particles.forEach((particle, i) => {
        let { start, next } = particle
        // Change position
        dummy.position.copy(start.lerp(next, (particle.startLife - particle.life) / 3));
        dummy.scale.set(0.5,0.5,0.5);
        dummy.lookAt(new THREE.Vector3(0,i%20,0));
        dummy.updateMatrix()
        // And apply the matrix to the instanced mesh
        mesh.current.setMatrixAt(i, dummy.matrix)

        
        particle.life -= 0.1 * (clock.elapsedTime - lastTime);
        // console.log(particle.life)
        if(particle.life <= 0) {
          particle.startLife = Math.random() * 3+0.3;
          particle.life = particle.startLife;
          particle.start = particle.next.clone();
          const y = i%20;
          const randCircle = Math.round(Math.random()*20) * Math.PI * 2 / 20;
          particle.next = new THREE.Vector3(Math.sin(randCircle) * 3 + Math.random()/10,
                                            y,
                                            Math.cos(randCircle) * 3 + Math.random()/10);
        } 
      })
      mesh.current.instanceMatrix.needsUpdate = true;
      
      lastTime = clock.elapsedTime;
    })
    return (
      <>
        <instancedMesh ref={mesh} geometry={gltf.scene.children[0].geometry} args={[null, null, count]}>
          <meshPhongMaterial attach="material" color="#33aaff" transparent={true} opacity={0.5}/>
        </instancedMesh>
      </>
    )
  }
  
  function Houses({ count, radius, amount }) {
    const mesh = useRef();
    const {scene} = useThree();
    let lastTime = 0;
  
    const dummy = useMemo(() => new THREE.Object3D(), []);

    let box = new THREE.BoxGeometry(2,8,2, 3, 5, 3);
    let geometry = box.attributes.position.array;
    let indices = box.index.array;
    let points = [];

    for(let i=0; i < count; i++) {
      let rand = Math.floor(Math.random() * indices.length / 3) * 3;
      
      let randPoint1 = new THREE.Vector3(geometry[indices[rand]*3]+Math.random() * 0.1, geometry[indices[rand]*3+1]+Math.random() * 0.1, geometry[indices[rand]*3+2]+Math.random() * 0.1);
      // let randPoint2 = new THREE.Vector3(geometry[indices[rand+1]*3], geometry[indices[rand+1]*3+1], geometry[indices[rand+1]*3+2]);
      // let randPoint3 = new THREE.Vector3(geometry[indices[rand+2]*3], geometry[indices[rand+2]*3+1], geometry[indices[rand+2]*3+2]);
      points.push(randPoint1);
    }
    
    const particles = useMemo(() => {
      const temp = [];
      for (let i = 0; i < count; i++) {
        const start = new THREE.Vector3(Math.cos(Math.PI * 2 / amount * i) * radius, 0, Math.sin(Math.PI * 2 / amount * i) * radius).add(points[i]);
        // const pos = points[i];

        temp.push({ start });
      }
      return temp
    }, [count])

    
    useFrame(({clock}) => {
      if(!mesh.current) return;
      
      // Go through each particle
      particles.forEach((particle, i) => {
        let { start, next } = particle
        // Change position
        dummy.position.copy(start);
        dummy.scale.set((Math.sin(i * 100 + clock.elapsedTime)+1.2) / 2,1, (Math.sin(i * 100 + clock.elapsedTime)+1.2) / 2);
        dummy.updateMatrix()
        // And apply the matrix to the instanced mesh
        mesh.current?.setMatrixAt(i, dummy.matrix)
      })
      mesh.current.instanceMatrix.needsUpdate = true;

      lastTime = clock.elapsedTime;
    })
    return (
      <>
        <instancedMesh ref={mesh} geometry={new THREE.BoxGeometry(2,4,2)} args={[null, null, count]}>
          <meshStandardMaterial attach="material" color="#33aaff"/>
        </instancedMesh>
      </>
    )
  }


  function Skyscraper({ count }) {
    const mesh = useRef()
    const gltf = useLoader(GLTFLoader, './hole.glb');
    const { camera } = useThree();
    let lastTime = 0;
    let lastImage = '';
  
    const dummy = useMemo(() => new THREE.Object3D(), [])
    
    useEffect(() => camera.position.set(-2,0,0))

    const particles = useMemo(() => {
      const temp = []
      for (let i = 0; i < count; i++) {
        const startLife = Math.random()*5;
        const life = startLife;
        const y = (i%5)*4;
        const randCircle = Math.round(Math.random()*20) * Math.PI * 2 / 20;
        const start = new THREE.Vector3(Math.sin(randCircle)* 15 + Math.random()/10,
                                        y,
                                        Math.cos(randCircle)* 15 + Math.random()/10);
        temp.push({ start, next: start, startLife, life});
      }
      return temp
    }, [count])
    
    useFrame(({clock, raycaster, camera, scene}) => {
      if(!mesh.current) return;
      // Go through each particle
      particles.forEach((particle, i) => {
        let { start, next } = particle
        // Change position
        dummy.position.copy(start.lerp(next, (particle.startLife - particle.life) / 7));
        dummy.scale.set(2,2,2);
        dummy.lookAt(new THREE.Vector3(0,(i%5)*4,0));
        dummy.updateMatrix();
        // And apply the matrix to the instanced mesh
        mesh?.current.setMatrixAt(i, dummy.matrix);

        
        particle.life -= 0.1 * (clock.elapsedTime - lastTime);
        // console.log(particle.life)
        if(particle.life <= 0) {
          particle.startLife = Math.random() * 5+2;
          particle.life = particle.startLife;
          particle.start = particle.next.clone();
          const y = (i%5)*4;
          const randCircle = Math.round(Math.random()*20) * Math.PI * 2 / 20;
          particle.next = new THREE.Vector3(Math.sin(randCircle) * 15 + Math.random()/10,
                                            y,
                                            Math.cos(randCircle) * 15 + Math.random()/10);
        } 
      })
      mesh.current.instanceMatrix.needsUpdate = true;
      // document.getElementById('image0').style.display = 'block';
      
      // raycaster.setFromCamera(mouse, camera);

      const intersects = raycaster.intersectObjects(scene.getObjectByName('tracable2').children, true );
      if(intersects.length > 0)
      {
        if(lastImage !== intersects[0].object.name)
        {
          document.getElementById('image').src = '';
          document.getElementById('image').src = intersects[0].object.name;
          let img = scene.getObjectByName('image');
          img.position.copy(intersects[0].object.position);
          img.rotation.copy(intersects[0].object.rotation);
          lastImage = intersects[0].object.name;
        }
      }


      lastTime = clock.elapsedTime;
    })
    return (
      <>
        <instancedMesh ref={mesh} geometry={gltf.scene.children[0].geometry} args={[null, null, count]}>
          <meshPhongMaterial attach="material" color="#77aaff" transparent={true} opacity={0.5}/>
        </instancedMesh>
      </>
    )
  }

  function Floor({ count, radius, amount }) {
    const mesh = useRef();
    const { scene, camera } = useThree();
    let lastTime = 0;
    let lastImage = '';
  
    const dummy = useMemo(() => new THREE.Object3D(), []);

    useEffect(() => camera.position.set(-2,5,0));
    
    const particles = useMemo(() => {
      const temp = [];
      for (let i = 0; i < count; i++) {
        const endLife = Math.random()/5;
        const life = 0;
        const start = new THREE.Vector3(Math.random() * 50 - 25, Math.random() * 0.1, Math.random() * 50 - 25);
        const next = new THREE.Vector3(Math.round(Math.random()*25) * 2 - 25, Math.random() * 0.1, Math.round(Math.random() * 25) * 2 - 25);
        // const pos = points[i];

        temp.push({ start, next, life, endLife });
      }
      return temp
    }, [count])

    
    useFrame(({clock, raycaster}) => {
      if(!mesh.current) return;
      
      // Go through each particle
      particles.forEach((particle, i) => {
        
        let { start, next } = particle;
        // Change position
        let lerping = start.lerp(next, particle.life);
        dummy.position.copy(lerping);
        dummy.scale.set(1,1,1);
        // dummy.lookAt(new THREE.Vector3(0,(i%5)*4,0));
        dummy.updateMatrix();
        // And apply the matrix to the instanced mesh
        mesh?.current.setMatrixAt(i, dummy.matrix);

        
        particle.life += 0.01 * (clock.elapsedTime - lastTime);
        // console.log(particle.life)
        if(particle.life >= particle.endLife) {
          particle.endLife = Math.random()/5;
          particle.life = 0;
          particle.start = particle.next.clone();
          particle.next = new THREE.Vector3(Math.round(Math.random()*25) * 2 - 25 + camera.position.x, Math.random() * 0.1, Math.round(Math.random() * 25) * 2 - 25 + camera.position.z);
        } 
      })
      mesh.current.instanceMatrix.needsUpdate = true;

      const intersects = raycaster.intersectObjects(scene.getObjectByName('tracable2').children, true );
      if(intersects.length > 0)
      {
        if(lastImage !== intersects[0].object.name)
        {
          document.getElementById('image').src = '';
          document.getElementById('image').src = intersects[0].object.name;
          let img = scene.getObjectByName('image');
          img.position.copy(intersects[0].object.position);
          img.rotation.copy(intersects[0].object.rotation);
          lastImage = intersects[0].object.name;
        }
      }

      lastTime = clock.elapsedTime;
    })
    return (
      <>
        <instancedMesh ref={mesh} geometry={new THREE.BoxGeometry(3,0.1,3)} args={[null, null, count]} renderOrder={2}>
          <meshStandardMaterial attach="material" color={0x224466} transparent={true} opacity={0.8} />
        </instancedMesh>
      </>
    )
  }
  
  
  export { Particles, Houses, Skyscraper, Floor };